import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiV1, setAccessToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/PasswordInput';
import { Loader2, LogIn, ShieldCheck } from 'lucide-react';
import { HireIqLogo } from '@/components/HireIqLogo';
import {
  PASSWORD_POLICY_HINT,
  TurnstileWidget,
  passwordMeetsPolicy,
} from '@/components/TurnstileWidget';
import { LOGIN } from '@/constants/testIds/auth';

/**
 * Provider-driven login page.
 * GET /auth/config decides Microsoft SSO vs email/password — never customer name.
 */
export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();
  const { setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [cfgErr, setCfgErr] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const [expiryWarningDays, setExpiryWarningDays] = useState(null);
  const [showResetForm, setShowResetForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const params = new URLSearchParams(loc.search);
  const next = params.get('next') || '/portal';
  const passwordSet = params.get('passwordSet') === '1';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiV1.get('/auth/config');
        if (!cancelled) setCfg(r.data);
      } catch (e) {
        if (!cancelled) {
          setCfgErr(e?.response?.data?.detail || e.message || 'Could not load login config');
          // Fallback: assume Entra so existing KL deploys keep working if config fails
          setCfg({
            provider: 'entra',
            supports_microsoft_sso: true,
            supports_password_login: false,
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onCaptcha = useCallback((token) => setCaptchaToken(token), []);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser?.(null);
  }, [setUser]);

  const handleMicrosoft = async () => {
    setBusy(true);
    setErr(null);
    try {
      const redirectUri = `${window.location.origin}/auth/callback`;
      const r = await apiV1.post('/auth/login/start', { redirect_uri: redirectUri });
      sessionStorage.setItem('hireiq:postLoginNext', next);
      window.location.assign(r.data.auth_url);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const handlePassword = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    setErr(null);
    if (cfg?.captcha_site_key && !captchaToken) {
      setErr('Please complete the captcha.');
      setBusy(false);
      return;
    }
    try {
      const r = await apiV1.post('/auth/login/password', {
        email: email.trim(),
        password,
        captcha_token: captchaToken || undefined,
      });
      const days = r.data.password_expires_in_days;
      setAccessToken(r.data.access_token);
      setUser?.(r.data.user);
      if (typeof days === 'number' && days >= 1 && days <= 7) {
        setExpiryWarningDays(days);
        setShowResetForm(false);
        setCurrentPassword(password);
        setBusy(false);
        setCaptchaToken(null);
        setCaptchaResetKey((k) => k + 1);
        return;
      }
      nav(next, { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setCaptchaToken(null);
      setCaptchaResetKey((k) => k + 1);
      setBusy(false);
    }
  };

  const handleContinue = () => {
    nav(next, { replace: true });
  };

  const handleChangePassword = async (ev) => {
    ev.preventDefault();
    setErr(null);
    if (!passwordMeetsPolicy(newPassword)) {
      setErr(PASSWORD_POLICY_HINT);
      return;
    }
    if (newPassword !== confirmPassword) {
      setErr('New password and confirmation do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setErr('New password must be different from the current password.');
      return;
    }
    if (cfg?.captcha_site_key && !captchaToken) {
      setErr('Please complete the captcha.');
      return;
    }
    setBusy(true);
    try {
      await apiV1.post('/auth/password/change', {
        email: email.trim(),
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
        captcha_token: captchaToken || undefined,
      });
      clearSession();
      setExpiryWarningDays(null);
      setShowResetForm(false);
      setPassword('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      nav('/login?passwordSet=1', { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setCaptchaToken(null);
      setCaptchaResetKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  // Branding is fixed product UI — never driven by LOGIN_TITLE / SUBTITLE / LOGO_URL env.
  const showMs = cfg?.supports_microsoft_sso;
  const showPw = cfg?.supports_password_login;
  const inExpiryWarning = typeof expiryWarningDays === 'number' && expiryWarningDays >= 1 && expiryWarningDays <= 7;

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border bg-card p-8 shadow-sm" data-testid="login-card">
        <div className="flex flex-col gap-3">
          <HireIqLogo size="xl" className="mx-auto max-w-[200px] !object-center" data-testid="login-brand-logo" />
          <div>
            <p className="text-lg font-semibold tracking-tight">Welcome to HireIQ</p>
          </div>
        </div>

        {passwordSet ? (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="login-password-set-banner">
            Password saved. Sign in with your email and new password.
          </div>
        ) : null}

        {!cfg && !cfgErr ? (
          <div className="mt-8 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : null}

        {showMs && !inExpiryWarning ? (
          <>
            <p className="mt-6 text-sm text-muted-foreground">
              Sign in with your Microsoft 365 account.
            </p>
            <Button
              onClick={handleMicrosoft}
              disabled={busy}
              className="mt-6 w-full gap-2"
              size="lg"
              data-testid="sso-login-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
              {busy ? 'Redirecting to Microsoft…' : 'Sign in with Microsoft'}
            </Button>
          </>
        ) : null}

        {showPw && !inExpiryWarning ? (
          <form onSubmit={handlePassword} className="mt-6 space-y-3" data-testid="password-login-form">
            <p className="text-sm text-muted-foreground">
              Sign in with the email and password for your HireIQ account.
            </p>
            <div>
              <label className="text-xs font-medium text-muted-foreground" htmlFor="login-email">Email</label>
              <Input
                id="login-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid={LOGIN.emailInput}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground" htmlFor="login-password">Password</label>
              <PasswordInput
                id="login-password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid={LOGIN.passwordInput}
              />
            </div>
            {cfg?.captcha_site_key ? (
              <TurnstileWidget
                siteKey={cfg.captcha_site_key}
                onToken={onCaptcha}
                resetKey={captchaResetKey}
              />
            ) : null}
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-xs text-primary underline-offset-2 hover:underline"
                data-testid={LOGIN.forgotPasswordLink}
              >
                Forgot password?
              </Link>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full gap-2"
              size="lg"
              data-testid={LOGIN.submitButton}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        ) : null}

        {inExpiryWarning ? (
          <div className="mt-6 space-y-3">
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid={LOGIN.expiryWarning}
            >
              {`⚠️ Your password will expire in ${expiryWarningDays} days. Please reset it now to maintain uninterrupted access to your account.`}
            </div>
            {!showResetForm ? (
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="default"
                  className="w-full"
                  onClick={() => {
                    setShowResetForm(true);
                    setErr(null);
                  }}
                  data-testid={LOGIN.resetPasswordButton}
                >
                  Reset Password
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleContinue}
                  data-testid={LOGIN.continueButton}
                >
                  Continue to HireIQ
                </Button>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-3" data-testid="login-change-password-form">
                <div>
                  <label className="text-xs font-medium text-muted-foreground" htmlFor="change-current">
                    Current password
                  </label>
                  <PasswordInput
                    id="change-current"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    data-testid={LOGIN.changeCurrentInput}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground" htmlFor="change-new">
                    New password
                  </label>
                  <PasswordInput
                    id="change-new"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    data-testid={LOGIN.changeNewInput}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground" htmlFor="change-confirm">
                    Confirm new password
                  </label>
                  <PasswordInput
                    id="change-confirm"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    matchAgainst={newPassword}
                    required
                    data-testid={LOGIN.changeConfirmInput}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">{PASSWORD_POLICY_HINT}</p>
                {cfg?.captcha_site_key ? (
                  <TurnstileWidget
                    siteKey={cfg.captcha_site_key}
                    onToken={onCaptcha}
                    resetKey={captchaResetKey}
                  />
                ) : null}
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full gap-2"
                  size="lg"
                  data-testid={LOGIN.changeSubmitButton}
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {busy ? 'Updating…' : 'Update password'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setShowResetForm(false);
                    setErr(null);
                  }}
                >
                  Back
                </Button>
              </form>
            )}
          </div>
        ) : null}

        {err && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="login-error">
            {typeof err === 'string' ? err : JSON.stringify(err)}
          </div>
        )}
        <div className="mt-6 flex items-start gap-2 rounded-lg bg-secondary/40 p-3 text-[11px] text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" />
          <p>
            {showPw
              ? 'Passwords are verified by HireIQ against a secure hash stored for this deployment. Your session is bound to this browser and expires after 14 days of inactivity.'
              : 'Sign-in is securely handled by Microsoft. We never store your password. Your session is bound to this browser and expires after 14 days of inactivity.'}
          </p>
        </div>
      </div>
    </div>
  );
}
